// @flow
import type { ViewDevice } from '../types';
import moment from 'moment-es6';
import type { Lesson } from '../types/index';
import type { Location as RouterLocation } from 'react-router';

export const scrollToAnchor = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
        element.scrollIntoView(true);
        location.hash = id;
    }
};

export const calculateViewDevice = (): ViewDevice => {
    if (window.matchMedia('(min-width: 769px)').matches) {
        return 'DESKTOP';
    } else {
        return 'MOBILE';
    }
};

export const getDeviceClass = (device: ViewDevice): string => {
    switch (device) {
        case 'MOBILE':
            return 'mobile';
        case 'DESKTOP':
            return 'desktop';
    }
    return '';
};

export const getLessonDateText = (lesson: Lesson): string => {
    const dateText = moment(lesson.start).format('YYYY/MM/DD');
    const dayOfWeek = moment(lesson.start).day();
    const dayOfWeekText = ['日', '月', '火', '水', '木', '金', '土'][dayOfWeek];
    return `${dateText}(${dayOfWeekText})`;
};

export const getShortLessonDateText = (lesson: Lesson): string => {
    const dateText = moment(lesson.start).format('MM/DD');
    const dayOfWeek = moment(lesson.start).day();
    const dayOfWeekText = ['日', '月', '火', '水', '木', '金', '土'][dayOfWeek];
    return `${dateText}(${dayOfWeekText})`;
};

export const getLessonTimeText = (lesson: Lesson): string => {
    const startTimeText = moment(lesson.start).format('HH:mm');
    const endTimeText = moment(lesson.end).format('HH:mm');
    return `${startTimeText}-${endTimeText}`;
};

export const getLessonDateTimeText = (lesson: Lesson): string => {
    const startTimeText = moment(lesson.start).format('HH:mm');
    const endTimeText = moment(lesson.end).format('HH:mm');
    return `${getLessonDateText(lesson)} ${startTimeText}-${endTimeText}`;
};

export const getShortLessonDateTimeText = (lesson: Lesson): string => {
    const startTimeText = moment(lesson.start).format('HH:mm');
    const endTimeText = moment(lesson.end).format('HH:mm');
    return `${getShortLessonDateText(lesson)} ${startTimeText}-${endTimeText}`;
};

export const srcParams = (src1x: string, src2x: ?string = null) => {
    if (!src2x) {
        return {
            src: src1x,
        };
    }
    return {
        src: src1x,
        srcSet: `${src1x} 1x, ${src2x} 2x`,
    };
};

export const setPageTitle = (
    title: string,
    suffix: string = ' | SHElikes（シーライクス）21世紀を生きる女性のためのレッスンクラブ'
) => {
    document.title = `${title}${suffix}`;
};

/**
 *
 # Canonicalize url for SEO, so that crawlers do not index minor url variants as different pages
 # e.g. The following patterns should be converted to https://she-inc.jp/likes/
 #  - https://she-inc.jp/likes
 #  - https://she-inc.jp/likes/
 #  - http://she-inc.jp/likes
 #  - http://she-inc.jp/likes/
 * @param origin the origin of url
 * @param routerLocation the location object injected from react-router
 * @returns {string} the canonicalized url
 */
export const canonicalizeUrl = (origin: string, routerLocation: RouterLocation) => {
    origin = origin.replace('http://', 'https://');
    const suffix = routerLocation.pathname.endsWith('/') ? '' : '/';
    return `${origin}${routerLocation.pathname}${suffix}${routerLocation.search || ''}${routerLocation.hash || ''}`;
};
